'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import type { Album } from '@/lib/albums-types';

interface BookAnimationProps {
  album: Album;
  memberColor: string;
}

export default function BookAnimation({ album, memberColor }: BookAnimationProps) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsOpen(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      {/* Mobile Layout */}
      <div className="md:hidden flex flex-col gap-6">
        {/* Back link */}
        <Link
          href="/albums"
          className="flex items-center gap-2 text-[#666666] hover:text-[#888888] transition-colors"
        >
          <span className="text-xs">← 返回專輯櫃</span>
        </Link>

        {/* Album Cover & Info */}
        <div className="flex flex-col items-center gap-4 p-6 rounded-lg bg-[#0D0D10]">
          <div
            className="w-[200px] h-[200px] rounded-lg overflow-hidden bg-[#1A1A1A]"
            style={{
              backgroundImage: `url(${album.coverUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          <div className="flex flex-col items-center gap-1">
            <span className="text-xs tracking-[2px] text-[#D4AF37] font-en">
              {album.year}
            </span>
            <h1 className="text-xl font-bold text-[#F5F5F0]">{album.title}</h1>
            <p className="text-xs text-[#888888]">
              {album.artist} · {album.albumType}
            </p>
          </div>
        </div>

        {/* Track List */}
        <div className="flex flex-col gap-4 p-4 rounded-lg bg-[#111115]">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold tracking-[2px] text-[#D4AF37] font-en">
              TRACK LIST
            </span>
            <span className="text-[10px] tracking-[1px] text-[#666666]">
              {album.tracks.length} TRACKS
            </span>
          </div>

          <div className="h-[1px] bg-[#D4AF37]/25" />

          <div className="flex flex-col">
            {album.tracks.map((track) => (
              <div
                key={track.number}
                className="flex items-center justify-between py-3 border-b border-white/[0.06] last:border-b-0"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="text-xs font-semibold text-[#D4AF37] font-en w-5">
                    {track.number.toString().padStart(2, '0')}
                  </span>
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <p className="text-sm text-[#F5F5F0] truncate">
                      {track.title}
                    </p>
                    {track.titleKo && (
                      <p className="text-[10px] text-[#888888] truncate">{track.titleKo}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button className="p-2 rounded-md bg-[#1A1A1A] border border-white/10 text-[#888888]">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </button>
                  {track.isTitle && (
                    <button className="px-3 py-1.5 rounded-md bg-[#D4AF37] text-[#0A0A0A] text-[10px] font-semibold">
                      歌詞
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between pt-2">
            <span className="text-[10px] text-[#555555]">發行：{album.releaseDate}</span>
            <span className="text-[10px] text-[#444444]">© YG Entertainment</span>
          </div>
        </div>
      </div>

      {/* Desktop Layout with Animation */}
      <div className="hidden md:flex justify-center items-center py-8" style={{ perspective: '2000px' }}>
        <div
          className="relative"
          style={{
            width: '1100px',
            height: '600px',
            transformStyle: 'preserve-3d',
          }}
        >
          {/* Left Page - Cover */}
          <div
            className="absolute left-0 top-0 w-[400px] h-full flex flex-col items-center justify-center gap-6 p-10 rounded-l-lg transition-all duration-1000 ease-out"
            style={{
              backgroundColor: '#0D0D10',
              boxShadow: isOpen ? '4px 0 20px rgba(0,0,0,0.25)' : '0 8px 40px rgba(0,0,0,0.6)',
              transformOrigin: 'right center',
            }}
          >
            <Link
              href="/albums"
              className="self-start flex items-center gap-2 text-[#666666] hover:text-[#888888] transition-colors"
            >
              <span className="text-xs">← 返回封面</span>
            </Link>

            <div
              className="w-[280px] h-[280px] rounded-[10px] overflow-hidden bg-[#1A1A1A]"
              style={{
                backgroundImage: `url(${album.coverUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />

            <div className="flex flex-col items-center gap-1.5">
              <span className="text-[13px] tracking-[2px] text-[#D4AF37] font-en">
                {album.year}
              </span>
              <h1 className="text-[28px] font-bold text-[#F5F5F0]">{album.title}</h1>
              <p className="text-sm text-[#888888]">
                {album.artist} · {album.albumType}
              </p>
            </div>
          </div>

          {/* Book Spine */}
          <div
            className={`absolute left-[400px] top-0 w-[2px] h-full transition-opacity duration-500 ${
              isOpen ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              background:
                'linear-gradient(180deg, transparent 0%, rgba(212,175,55,0.25) 30%, rgba(212,175,55,0.25) 70%, transparent 100%)',
              transitionDelay: '0.5s',
            }}
          />

          {/* Right Page - Track List */}
          <div
            className="absolute left-[402px] top-0 h-full flex flex-col gap-6 p-10 px-12 rounded-r-lg border border-white/[0.03] transition-all duration-1000 ease-out"
            style={{
              width: 'calc(100% - 402px)',
              backgroundColor: '#111115',
              boxShadow: '-4px 0 20px rgba(0,0,0,0.2)',
              transformOrigin: 'left center',
              transform: isOpen ? 'rotateY(0deg)' : 'rotateY(-90deg)',
              opacity: isOpen ? 1 : 0,
            }}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold tracking-[2px] text-[#D4AF37] font-en">
                TRACK LIST
              </span>
              <span className="text-xs tracking-[1px] text-[#666666]">
                {album.tracks.length} TRACKS
              </span>
            </div>

            <div className="h-[1px] bg-[#D4AF37]/25" />

            <div className="flex-1 flex flex-col overflow-y-auto">
              {album.tracks.map((track, index) => (
                <div
                  key={track.number}
                  className="flex items-center justify-between py-4 border-b border-white/[0.06] last:border-b-0 transition-all duration-500"
                  style={{
                    opacity: isOpen ? 1 : 0,
                    transform: isOpen ? 'translateX(0)' : 'translateX(20px)',
                    transitionDelay: `${0.8 + index * 0.05}s`,
                  }}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-semibold text-[#D4AF37] font-en w-6">
                      {track.number.toString().padStart(2, '0')}
                    </span>
                    <div className="flex flex-col gap-1">
                      <p className="text-[#F5F5F0] flex items-center gap-2">
                        {track.title}
                        {track.titleKo && (
                          <span className="text-[#888888]">({track.titleKo})</span>
                        )}
                      </p>
                      {track.duration && (
                        <span className="text-xs text-[#555555]">{track.duration}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <button className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#1A1A1A] border border-white/10 text-[#888888] hover:text-white transition-colors">
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </button>
                    {track.isTitle && (
                      <button className="px-4 py-2 rounded-md bg-[#D4AF37] text-[#0A0A0A] text-xs font-semibold hover:bg-[#E5A500] transition-colors">
                        歌詞
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between pt-4">
              <span className="text-xs text-[#555555]">發行日期：{album.releaseDate}</span>
              <span className="text-[11px] text-[#444444]">© YG Entertainment</span>
            </div>
          </div>

          {/* Book Cover (folds away) */}
          <div
            className="absolute left-[400px] top-0 w-[calc(100%-400px)] h-full rounded-r-lg transition-all duration-1000 ease-out"
            style={{
              backgroundColor: '#0D0D10',
              backgroundImage: `url(${album.coverUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              transformOrigin: 'left center',
              transform: isOpen ? 'rotateY(90deg)' : 'rotateY(0deg)',
              opacity: isOpen ? 0 : 1,
              boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
              pointerEvents: isOpen ? 'none' : 'auto',
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent rounded-r-lg flex flex-col items-center justify-end pb-16">
              <span className="text-[13px] tracking-[2px] text-[#D4AF37] font-en mb-2">
                {album.year}
              </span>
              <h2 className="text-4xl font-bold text-[#F5F5F0] mb-2">{album.title}</h2>
              <p className="text-sm text-[#888888]">{album.artist}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
